// Meta Pixel for raisedbeef.com (owner, 2026-10-18), to measure which ads lead
// to sign-ups and paid plans. Pixel id 1836229154048406 (Events Manager).
//
// ── WHY A MODULE AND NOT THE SNIPPET IN index.html ───────────────────────────
// The site builds nine HTML pages, each with its own bundle (index.html and the
// eight entry-*.tsx). A pasted snippet would have to be copied into every one,
// and would also run on previews, the TV lobby board, /manage, and the native
// barn apps. Every entry already calls startWebAnalytics() last, which now
// calls startMetaPixel() — one place, once per page load, and the same host,
// path and environment rules as Google Analytics (src/lib/analytics.ts).
//
// ── WHAT IS DIFFERENT FROM META'S OFFICIAL SNIPPET ───────────────────────────
// · The official base code is here unchanged in behavior (queue, fbevents.js
//   from connect.facebook.net, init, PageView) — but it only runs AFTER the
//   visitor says yes. Until then nothing is requested from facebook.com.
// · The <noscript> tracking pixel is NOT used. It fires without JavaScript and
//   without consent, which would defeat the choice.
// · Automatic advanced matching and automatic button/form events are OFF
//   (`set autoConfig false`). Left on, Meta's script reads the email and name
//   boxes on the sign-up form. Nothing about a person is sent from here.
// · Meta's own history-change PageView is off (`disablePushState`); the router
//   reports each route change through trackMetaPageView so none counts twice.
//
// ── CONSENT ──────────────────────────────────────────────────────────────────
// · Stored per browser in localStorage as "granted" or "denied". Unset = ask.
// · A Global Privacy Control signal is treated as "denied" and no banner shows.
// · The footer's "Ad choices" link (data-rb-ad-choices) reopens the question;
//   switching to "denied" after a yes sends `consent revoke` and stops events.
//
// ── WHERE IT MUST NOT RUN ────────────────────────────────────────────────────
// Only raisedbeef.com / www, only a production build. Never the native apps or
// a VITE_BARN_APP build (the store privacy answers declare no tracking), the
// lobby, /manage, /admin, /board, previews, or localhost. And never on a page
// whose URL carries sign-in material (see hasAuthMaterial).
//
// ── WHAT IT NEVER SENDS ──────────────────────────────────────────────────────
// No email, name, password, card or Stripe ids. Only: the page view, "account
// created" (CompleteRegistration), and "paid plan confirmed" (Subscribe) with
// the plan, the price and the currency. See src/lib/metaConversions.ts.

export const META_PIXEL_ID = "1836229154048406";

/** The hosts the pixel may run on. A Vercel preview and localhost are not here
 *  on purpose: test clicks should not reach the ad account. */
export const META_HOSTS = new Set(["raisedbeef.com", "www.raisedbeef.com", "cattlemarketcentral.com", "www.cattlemarketcentral.com"]);

const EXCLUDED_PATHS = [/^\/manage(\/|$)/, /^\/admin(\/|$)/, /^\/lobby(\/|$)/, /^\/board(\/|$)/];

/** Is this page one the pixel may run on? Pure, for tests. */
export function pixelAllowed(hostname: string, pathname: string): boolean {
  if (!META_HOSTS.has(hostname)) return false;
  return !EXCLUDED_PATHS.some((re) => re.test(pathname));
}

/** Does the address carry sign-in material? Supabase can land a person back on
 *  /account with tokens in the #fragment or a one-time ?code=, and a password
 *  reset carries ?reset=1. The pixel reports the page's full address, so a
 *  view of such a page is skipped — the next route change counts instead. */
export function hasAuthMaterial(search: string, hash: string): boolean {
  const re = /(^|[?#&])(access_token|refresh_token|provider_token|token_hash|code|reset|error_description)=/;
  const kind = /[?#&]type=(recovery|signup|magiclink|invite|email_change)/;
  return re.test(search) || re.test(hash) || kind.test(search) || kind.test(hash);
}

export type AdConsent = "granted" | "denied" | "unset";

/** The stored choice, folded with Global Privacy Control. Pure, for tests. */
export function resolveConsent(stored: string | null, gpc: boolean): AdConsent {
  if (gpc) return "denied";
  return stored === "granted" || stored === "denied" ? stored : "unset";
}

const CONSENT_KEY = "rb_ad_consent";
let memoryConsent: AdConsent | null = null;

function gpcOn(): boolean {
  return typeof navigator !== "undefined"
    && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

export function readAdConsent(): AdConsent {
  let stored: string | null = memoryConsent;
  if (!stored) {
    try { stored = window.localStorage.getItem(CONSENT_KEY); } catch { /* storage blocked: ask each visit */ }
  }
  return resolveConsent(stored, gpcOn());
}

function storeAdConsent(value: "granted" | "denied" | null): void {
  memoryConsent = value;
  try {
    if (value) window.localStorage.setItem(CONSENT_KEY, value);
    else window.localStorage.removeItem(CONSENT_KEY);
  } catch { /* the choice still holds for this page load */ }
}

// ── the official base code, typed ────────────────────────────────────────────
interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
  allowDuplicatePageViews?: boolean;
}
type FbWindow = Window & { fbq?: Fbq; _fbq?: Fbq; Capacitor?: { isNativePlatform?: () => boolean } };

// A barn's app shown on the web — raisedbeef.com/mitchell, or a ?barn= preview —
// is the BARN's app, not RaisedBeef's marketing site, and its store privacy
// answers declare no tracking. Owner 2026-11-02, on the Mitchell demo: "why is
// the meta policy showing up on the mitchell demo app right now?" The path
// check below could not see it: barn addresses are top-level words from the
// barn registry, not a fixed prefix. main.tsx (the only entry that serves barn
// pages) marks the document instead, before analytics start, so this module
// does not have to import the whole registry into every page bundle.
let barnPage = false;

/** Mark this document as a barn app page: no pixel, no consent banner. */
export function markBarnPage(): void {
  barnPage = true;
}

/** True in a build and a browser where the pixel may ever load. */
function allowedEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.PROD) return false;
  if (barnPage) return false;
  if ((import.meta.env.VITE_BARN_APP as string | undefined)?.trim()) return false;
  if ((window as FbWindow).Capacitor?.isNativePlatform?.()) return false;
  return pixelAllowed(window.location.hostname, window.location.pathname);
}

let loaded = false;

/** Load the pixel once, and only for a visitor who said yes. */
function load(): Fbq | null {
  if (!allowedEnvironment() || readAdConsent() !== "granted") return null;
  const w = window as FbWindow;
  if (loaded) return w.fbq ?? null;
  loaded = true;
  if (!w.fbq) {
    // Meta's base code, as published: a queueing function until fbevents.js
    // arrives and replaces callMethod. It pushes the `arguments` object, not an
    // array — keep it this shape, as with gtag in analytics.ts.
    const n = function fbq() {
      // eslint-disable-next-line prefer-rest-params
      const args = arguments as unknown;
      if (n.callMethod) n.callMethod(...(args as unknown[]));
      else n.queue.push(args);
    } as Fbq;
    w.fbq = n;
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    n.disablePushState = true;
    n.allowDuplicatePageViews = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  const fbq = w.fbq as Fbq;
  fbq("set", "autoConfig", false, META_PIXEL_ID);
  fbq("init", META_PIXEL_ID);
  return fbq;
}

let lastView = "";

/** One PageView for this address, if the pixel may run. Never throws. */
export function trackMetaPageView(pathname: string, search: string): void {
  try {
    if (typeof window === "undefined" || barnPage || !pixelAllowed(window.location.hostname, pathname)) return;
    if (hasAuthMaterial(search, window.location.hash)) return;
    const key = `${pathname}${search}`;
    if (key === lastView) return;
    const fbq = load();
    if (!fbq) return;
    lastView = key;
    fbq("track", "PageView");
  } catch {
    // Advertising measurement must never break a page.
  }
}

/** A standard event, with an id so a later server-side copy can be matched and
 *  not double-counted. Returns whether it was actually sent: the caller marks
 *  an event "done" only then, so a visitor who has not said yes yet is not
 *  silently counted as reported. */
export function trackMetaEvent(
  event: "CompleteRegistration" | "Subscribe",
  params: Record<string, string | number | boolean>,
  eventId: string,
): boolean {
  try {
    const fbq = load();
    if (!fbq) return false;
    fbq("track", event, params, { eventID: eventId });
    return true;
  } catch {
    return false;
  }
}

// ── the question ─────────────────────────────────────────────────────────────
const BAR_ID = "rb-ad-consent";

function closeBar(): void {
  document.getElementById(BAR_ID)?.remove();
}

/** Record the answer, and act on it right away. */
export function setAdConsent(value: "granted" | "denied"): void {
  storeAdConsent(value);
  closeBar();
  try {
    if (value === "granted") {
      trackMetaPageView(window.location.pathname, window.location.search);
    } else {
      (window as FbWindow).fbq?.("consent", "revoke");
    }
  } catch { /* never break a page */ }
}

function showAdQuestion(): void {
  if (document.getElementById(BAR_ID)) return;
  const bar = document.createElement("div");
  bar.id = BAR_ID;
  bar.setAttribute("role", "dialog");
  bar.setAttribute("aria-label", "Ad measurement");
  bar.style.cssText =
    "position:fixed;left:0;right:0;bottom:0;z-index:2147483000;background:#102a54;color:#fff;" +
    "border-top:2px solid #e4ae3f;padding:14px 16px;display:flex;flex-wrap:wrap;gap:12px;" +
    "align-items:center;justify-content:center;font:15px/1.45 -apple-system,'Segoe UI',system-ui,Arial,sans-serif";

  const text = document.createElement("div");
  text.style.cssText = "flex:1 1 360px;max-width:720px";
  text.textContent =
    // Owner's wording (2026-10-18). It names Meta and says what is reported,
    // purchases included: Meta's Business Tools Terms expect that notice, and a
    // choice is only a choice if the person knows who gets the data.
    "Help us reach more cattlemen. With your OK, Meta tells us which of our ads led to a visit, sign-up or purchase. " +
    "We never send your name, email or payment details. ";
  const more = document.createElement("a");
  more.href = "/privacy";
  more.textContent = "Privacy";
  more.style.cssText = "color:#e4ae3f;text-decoration:underline";
  text.appendChild(more);

  const mk = (label: string, value: "granted" | "denied", primary: boolean) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.style.cssText =
      "min-height:44px;padding:0 20px;border-radius:8px;font:600 15px/1 inherit;cursor:pointer;" +
      (primary ? "background:#e4ae3f;color:#0d2144;border:2px solid #e4ae3f" : "background:transparent;color:#fff;border:2px solid #fff");
    b.addEventListener("click", () => setAdConsent(value));
    return b;
  };
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:10px;flex-wrap:wrap";
  row.append(mk("No thanks", "denied", false), mk("OK", "granted", true));

  bar.append(text, row);
  document.body.appendChild(bar);
}

/** The footer's "Ad choices" link: ask again, whatever was chosen before. */
export function openAdChoices(): void {
  if (!allowedEnvironment() || gpcOn()) return;
  storeAdConsent(null);
  showAdQuestion();
}

let started = false;

/** Called once per page load by startWebAnalytics(). */
export function startMetaPixel(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  try {
    if (!allowedEnvironment()) return;
    document.addEventListener("click", (e) => {
      const link = (e.target as Element | null)?.closest?.("[data-rb-ad-choices]");
      if (!link) return;
      e.preventDefault();
      openAdChoices();
    });
    const consent = readAdConsent();
    if (consent === "unset") showAdQuestion();
    // The first view waits a moment, as AnalyticsTracker does: by then the
    // Supabase client has taken any sign-in material out of the address.
    window.setTimeout(() => trackMetaPageView(window.location.pathname, window.location.search), 800);
  } catch {
    // Advertising measurement must never break a page.
  }
}
